<script setup lang="ts">
import { setSupportWidgetMounted } from '@/composables/useSupportWidget';
import {
    MILO_LAUNCHER_SAFE_AREA_PROPERTY,
    miloLauncherSafeArea,
    resolveMiloSupportWidgetConfig,
} from '@/config/miloSupportWidget';
import { resolveMiloSupportPageContext } from '@/config/miloSupportPageContext';
import { RouteNames } from '@/router/names';
import { useAuthStore } from '@/stores/auth';
import { useCatalogsStore } from '@/stores/catalogs';
import { useCustomersStore } from '@/stores/customers';
import { useProjectsStore } from '@/stores/projects';
import { useTimeEntriesStore } from '@/stores/timeEntries';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const WIDGET_ELEMENT_NAME = 'milo-support-chat';
const WIDGET_MARKER_ATTRIBUTE = 'data-milo-app-support-widget';
const supportWidgetConfig = resolveMiloSupportWidgetConfig(import.meta.env);
// Chat and telemetry go through the authenticated milo-api proxy (/api/ai/*),
// which injects the user principal and gateway secret server-side. Direct
// posts to ai.milomoves.app on the web-app channel are rejected with 401.

const props = defineProps<{
    authenticated: boolean;
}>();

type MiloChatActionCompletedDetail = {
    tool?: string;
    resource?: {
        type?: MiloCompletedResourceType;
        id?: string;
    };
};

type MiloCompletedResourceType = 'invoice' | 'quote' | 'catalog_item' | 'customer' | 'project' | 'time_entry';

const auth = useAuthStore();
const { activeCompany, user } = storeToRefs(auth);
const { locale, t } = useI18n();
const route = useRoute();
const router = useRouter();
const catalogsStore = useCatalogsStore();
const customersStore = useCustomersStore();
const projectsStore = useProjectsStore();
const timeEntriesStore = useTimeEntriesStore();

// The company the user has selected in the switcher. Forwarded to the widget
// so AI chat scopes to it instead of falling back to the user's oldest company
// (milo-api's User::activeCompany default). Resolved via the store so it always
// matches what the UI shows, even before activeCompanyId is persisted.
const activeCompanyId = computed(() => activeCompany.value?.id ?? null);
const supportWidgetUserId = computed(() => user.value?.id ?? null);
const supportWidgetUserName = computed(() => user.value?.first_name ?? '');
const supportWidgetLocale = computed(() => normalizeSupportWidgetLocale(locale.value));
const supportWidgetPageContext = computed(() => {
    // Reading the normalized locale makes this computed refresh when the app
    // language changes, even though vue-i18n's overloaded `t` function itself
    // is stable.
    void supportWidgetLocale.value;
    return resolveMiloSupportPageContext(route.name, route.path, route.meta.title, (key, values) =>
        t(key, values ?? {}),
    );
});

// Support is not a paid feature (MILO-417): every signed-in user gets the
// widget, whatever their plan. What the plan decides is whether Milo may *act*
// for them — milo-api pins accounts without ai_bots to the gateway's support
// mode and withholds the token the action surface needs, so a Starter account
// gets knowledge-base answers and a human handoff but no tool calls.
const shouldMountWidget = computed(() => {
    return import.meta.env.VITE_MILO_SUPPORT_WIDGET_ENABLED === 'true' && props.authenticated;
});

const loadWidgetModule = async (): Promise<void> => {
    if (customElements.get(WIDGET_ELEMENT_NAME)) return;
    await import(/* @vite-ignore */ supportWidgetConfig.src);
};

let mountGeneration = 0;

const mountWidget = async (): Promise<void> => {
    if (
        !shouldMountWidget.value ||
        document.querySelector(`${WIDGET_ELEMENT_NAME}[${WIDGET_MARKER_ATTRIBUTE}]`) ||
        auth.isAdmin()
    ) {
        return;
    }

    const generation = ++mountGeneration;
    await loadWidgetModule();
    if (generation !== mountGeneration || !shouldMountWidget.value) return;
    if (document.querySelector(`${WIDGET_ELEMENT_NAME}[${WIDGET_MARKER_ATTRIBUTE}]`)) return;

    const widget = document.createElement(WIDGET_ELEMENT_NAME);
    widget.setAttribute(WIDGET_MARKER_ATTRIBUTE, 'true');
    if (supportWidgetUserId.value) widget.setAttribute('user-id', supportWidgetUserId.value);
    widget.setAttribute('user-name', supportWidgetUserName.value);
    if (activeCompanyId.value) {
        widget.setAttribute('company-id', activeCompanyId.value);
    }
    widget.setAttribute('endpoint', supportWidgetConfig.endpoint);
    widget.setAttribute('telemetry-endpoint', supportWidgetConfig.telemetryEndpoint);
    widget.setAttribute('transcribe-endpoint', supportWidgetConfig.transcribeEndpoint);
    widget.setAttribute('channel', 'web-app');
    widget.setAttribute('locale', supportWidgetLocale.value);
    // Milo API proxy routes (/api/ai/*) sit behind auth:sanctum, so widget
    // fetches must carry the session cookie and X-XSRF-TOKEN header.
    widget.setAttribute('with-credentials', '');
    widget.setAttribute('csrf-cookie', 'XSRF-TOKEN');
    widget.addEventListener('milo-chat-action-completed', handleWidgetActionCompleted as EventListener);
    document.body.append(widget);
    syncSupportWidgetPageContext();
    // The bridge's own `ready` flag is untracked global state, so consumers that
    // rendered before this point would never learn the widget arrived. This is
    // the moment they need, and this component is the only one that knows it.
    setSupportWidgetMounted(true);
    reserveLauncherSafeArea();
};

const unmountWidget = (): void => {
    mountGeneration++;
    releaseLauncherSafeArea();
    document.querySelectorAll(`${WIDGET_ELEMENT_NAME}[${WIDGET_MARKER_ATTRIBUTE}]`).forEach((widget) => {
        widget.removeEventListener('milo-chat-action-completed', handleWidgetActionCompleted as EventListener);
        widget.remove();
    });
    setSupportWidgetMounted(false);
};

/**
 * The launcher is a fixed button in the bottom-right corner, so the app keeps
 * that corner free instead of letting the button dodge whatever lands under it
 * (MILO-438). The layouts reserve this as trailing space, which puts the end of
 * every page — where the form actions live — above the button. Dodging was both
 * incomplete (it re-measured against the button's resting place, never against
 * where it had just moved to) and visibly jumpy, since every scroll, resize and
 * DOM mutation recomputed the offset.
 */
function reserveLauncherSafeArea(): void {
    document.documentElement.style.setProperty(MILO_LAUNCHER_SAFE_AREA_PROPERTY, miloLauncherSafeArea());
}

function releaseLauncherSafeArea(): void {
    document.documentElement.style.removeProperty(MILO_LAUNCHER_SAFE_AREA_PROPERTY);
}

// v0.8.0 observes identity changes and clears drafts, pending work and local
// history before accepting input under the new user or administration.
watch([supportWidgetUserId, activeCompanyId], ([userId, companyId]) => {
    document.querySelectorAll(`${WIDGET_ELEMENT_NAME}[${WIDGET_MARKER_ATTRIBUTE}]`).forEach((widget) => {
        if (userId) widget.setAttribute('user-id', userId);
        else widget.removeAttribute('user-id');
        if (companyId) {
            widget.setAttribute('company-id', companyId);
        } else {
            widget.removeAttribute('company-id');
        }
    });
});

watch(supportWidgetUserName, (name) => {
    document.querySelectorAll(`${WIDGET_ELEMENT_NAME}[${WIDGET_MARKER_ATTRIBUTE}]`).forEach((widget) => {
        widget.setAttribute('user-name', name);
    });
});

// Keep the live widget in the same language as the app. The widget normalizes
// this to the gateway contract (`nl-NL` / `en-US`) and sends it as
// context.locale on every chat request.
watch(supportWidgetLocale, (widgetLocale) => {
    document.querySelectorAll(`${WIDGET_ELEMENT_NAME}[${WIDGET_MARKER_ATTRIBUTE}]`).forEach((widget) => {
        widget.setAttribute('locale', widgetLocale);
    });
});

// The host owns page context. Keep it aligned with Vue Router so the empty
// assistant thread offers useful tasks for the page that is actually visible.
watch(supportWidgetPageContext, () => {
    if (shouldMountWidget.value) syncSupportWidgetPageContext();
});

// Both hops are optional on purpose. The bundle is pinned per environment and
// only publishes setPageContext from v0.6.0, so on an older pin the second `?.`
// is the difference between losing the page context and an unhandled rejection
// out of mountWidget() that takes the whole app boot with it.
function syncSupportWidgetPageContext(): void {
    window.MiloSupportChat?.setPageContext?.(supportWidgetPageContext.value);
}

function normalizeSupportWidgetLocale(value: string): 'nl-NL' | 'en-US' {
    return value.trim().toLowerCase().startsWith('en') ? 'en-US' : 'nl-NL';
}

function handleWidgetActionCompleted(event: Event): void {
    const detail = (event as CustomEvent<MiloChatActionCompletedDetail>).detail;
    const resource = detail?.resource;
    if (!resource?.type) return;

    refreshCompletedResource(resource.type);
    navigateCompletedResource(resource);
}

function refreshCompletedResource(type: MiloCompletedResourceType): void {
    const refreshers: Partial<Record<MiloCompletedResourceType, () => Promise<unknown>>> = {
        catalog_item: () => catalogsStore.fetchItems(),
        customer: () => customersStore.loadPage(customersStore.currentPage),
        project: () => projectsStore.loadPage(projectsStore.currentPage),
        time_entry: () => timeEntriesStore.loadPage(timeEntriesStore.currentPage),
    };
    void refreshers[type]?.();
}

function navigateCompletedResource(resource: NonNullable<MiloChatActionCompletedDetail['resource']>): void {
    if (!resource.id) return;

    const routes: Partial<Record<MiloCompletedResourceType, string>> = {
        invoice: RouteNames.InvoiceView,
        quote: RouteNames.QuoteView,
    };
    const routeName = routes[resource.type];
    if (!routeName) return;

    void router.push({ name: routeName, params: { id: resource.id } });
}

onMounted(() => {
    void mountWidget();
});

watch(shouldMountWidget, (enabled) => {
    if (enabled) {
        void mountWidget();
        return;
    }

    unmountWidget();
});

onUnmounted(() => {
    unmountWidget();
});
</script>
